import type { NPC } from '../../../shared/CharacterTypes';
import { NPC_CONFIG } from '../../../shared/constants/EntityConfig';
import { GAME_CONSTANTS } from '../../constants/GameConstants';
import { GhostRenderer, type HumanoidAppearance } from '../GhostRenderer';
import { appearanceForNpc } from '../CoilNpcAppearances';
import { HUMANOID_GEOMETRY, bodyScaleFor } from '../parts/HumanoidGeometry';
import { normalizePortraitSize, preparePortraitSvg } from '../svg/scopeSvgIds';
import { applyWardrobePortraitCrop } from '../hushWardrobe/plan';
import { hushCastPosed } from '../npcAppearances/hushNookCast';

/**
 * The two places plan-12 NPC art meets the game: the world box a texture is
 * drawn into, and the dialog portrait. NPCRenderer, NpcPortrait and the
 * offline harness (scripts/vqa-npc-art.ts) all go through these functions.
 */

/** Where and how large to draw an NPC texture, relative to its hitbox top-left. */
export interface NpcWorldBox {
  width: number;
  height: number;
  centerX: number;
  centerY: number;
  /** Nameplate anchor (same convention as the legacy center + NPC_TEXT_OFFSET_Y). */
  labelY: number;
}

/** Art NPCs stand ~10% taller than the player's 72 px render (DJ, 2026-10-07). */
export const NPC_ART_BOX = 79;
/** Ground contact and head top in the canonical humanoid viewBox (pre body-size). */
const FOOT_Y = 114;
const HEAD_TOP_Y = 4;
/** Legacy gap between the nameplate anchor and the drawn head top, kept for art NPCs. */
const LABEL_GAP = 20;

/** Fraction of the square texture's height at a viewBox y (the 140×120 art is letterboxed). */
function textureFraction(viewBoxY: number, bodyScale: number): number {
  const { viewBox, characterPivot } = HUMANOID_GEOMETRY;
  const scaled = characterPivot.y + (viewBoxY - characterPivot.y) * bodyScale;
  const letterbox = (viewBox.width - viewBox.height) / 2;
  return (letterbox + scaled) / viewBox.width;
}

/**
 * Legacy NPCs stretch the square texture over the 60×50 hitbox (a ~17%
 * vertical squash, smaller than the player). Art NPCs draw it undistorted at
 * player scale with the feet planted on the hitbox bottom; breathing, lean and
 * rock pivot on the feet so nothing slides.
 */
export function npcWorldBox(
  _npc: NPC,
  baseWidth: number,
  baseHeight: number,
  frame?: { width: number; height: number; angle: number },
  appearance?: HumanoidAppearance,
): NpcWorldBox {
  if (!appearance?.humanoid?.art) {
    return {
      width: frame?.width ?? baseWidth,
      height: frame?.height ?? baseHeight,
      centerX: baseWidth / 2,
      centerY: baseHeight / 2,
      labelY: baseHeight / 2 + GAME_CONSTANTS.UI.NPC_TEXT_OFFSET_Y,
    };
  }
  const scaleX = frame ? frame.width / baseWidth : 1;
  const scaleY = frame ? frame.height / baseHeight : 1;
  const width = NPC_ART_BOX * scaleX;
  const height = NPC_ART_BOX * scaleY;
  const bodyScale = bodyScaleFor(appearance.bodySize);
  const footFromCenter = height * (textureFraction(FOOT_Y, bodyScale) - 0.5);
  const theta = ((frame?.angle ?? 0) * Math.PI) / 180;
  // Rotating about the sprite centre swings the feet; move the centre so they stay put.
  const centerX = baseWidth / 2 + footFromCenter * Math.sin(theta);
  const centerY = baseHeight + 1 - footFromCenter * Math.cos(theta);
  const headTop = centerY + height * (textureFraction(HEAD_TOP_Y, bodyScale) - 0.5);
  return { width, height, centerX, centerY, labelY: headTop - LABEL_GAP };
}

/** Caption layout follows the current body position/size, not cosmetic idle
 * breathing and rocking. Otherwise tiny animated height crossings swap the
 * priority of adjacent names each time the shared layout solves. */
export function npcCaptionAnchor(
  npc: NPC, baseWidth: number, baseHeight: number, appearance?: HumanoidAppearance,
): { x: number; y: number } {
  const rest = npcWorldBox(npc, baseWidth, baseHeight, undefined, appearance);
  return { x: (npc.position.x || 0) + rest.centerX, y: (npc.position.y || 0) + rest.labelY };
}

/** The NPC's appearance as both world and portrait resolve it (dialogue role → world sprite). */
export function npcAppearanceFor(npcId: string | undefined, npcType: string | undefined): HumanoidAppearance {
  return appearanceForNpc(npcId, (npcId ? NPC_CONFIG[npcId]?.sprite : undefined) || npcType);
}

/** Dialog/guidance portrait: the world art itself, bust-framed for art NPCs. `pose` only changes NPCs with
 *  wardrobe pose variants (Pip and Mallow); everyone else renders as in the world. */
export function renderNpcPortraitSvg(
  npcId: string | undefined, npcType: string | undefined, size: number, namespace: string, pose: 'idle' | 'talk' | 'gesture' = 'idle',
): string {
  const appearance = (pose !== 'idle' && hushCastPosed(npcId, pose)) || npcAppearanceFor(npcId, npcType);
  const renderSize = normalizePortraitSize(size);
  const svg = appearance.humanoid?.art
    ? GhostRenderer.render(appearance, renderSize, { frame: 'bust' })
    : GhostRenderer.render(appearance, renderSize);
  // Wardrobe NPCs widen the bust so the cap and held prop fit; every other portrait is returned unchanged.
  return preparePortraitSvg(applyWardrobePortraitCrop(svg, appearance), namespace);
}
